<div
  #chartScroll
  class="overflow-x-auto pb-lg"
  data-testid="department-org-chart">
  <ul
    class="flex w-max min-w-full justify-center *:pt-0 *:before:hidden *:after:hidden">
    @for (node of roots(); track node.department.id) {
      <ng-container
        *ngTemplateOutlet="departmentBranch; context: { $implicit: node }" />
    }
  </ul>
</div>

<ng-template #departmentBranch let-node>
  @let isExpanded = expandAll() || expandedIds().has(node.department.id);
  @let positions = positionForests().get(node.department.id) ?? noPositions;
  @let hasChildren = node.children.length > 0 || positions.length > 0;
  <li
    class="relative flex flex-col items-center px-sm pt-xl before:absolute before:top-0 before:end-1/2 before:h-xl before:w-1/2 before:border-t before:border-neutral-300 before:content-[''] after:absolute after:top-0 after:start-1/2 after:h-xl after:w-1/2 after:border-s after:border-t after:border-neutral-300 after:content-[''] first:before:border-t-0 last:after:border-t-0 only:before:hidden">
    <div
      class="flex items-center gap-sm min-w-44 max-w-56 p-md bg-white border border-neutral-200 rounded-lg"
      [class.opacity-50]="!node.isMatch"
      [attr.data-testid]="'org-chart-department-' + node.department.id">
      <button
        type="button"
        class="flex flex-1 items-center gap-md min-w-0 text-start cursor-pointer"
        [attr.data-testid]="'org-chart-select-' + node.department.id"
        (click)="selectDepartment.emit(node.department)">
        @if (node.department.image) {
          <img
            [src]="node.department.image.url"
            [alt]="node.department.image.name"
            width="28"
            height="28"
            class="rounded-sm size-7" />
        } @else {
          <span
            class="rounded-sm bg-primary-50 text-primary-500 flex justify-center items-center size-7 shrink-0 text-xs">
            {{ node.department.name | avatar }}
          </span>
        }
        <span
          class="text-sm font-semibold text-neutral-900 truncate"
          libTooltip
          [libTooltip]="node.department.name"
          [hasCloseIcon]="false"
          placement="top"
          toolTipAction="mouseenter"
          [toolTipClass]="nameTooltipClass">
          {{ node.department.name }}
        </span>
      </button>
      @if (hasChildren) {
        <button
          type="button"
          class="size-6 flex items-center justify-center rounded-sm border border-neutral-200 cursor-pointer"
          [attr.aria-expanded]="isExpanded"
          [attr.aria-label]="
            (isExpanded
              ? 'department_tree_collapse_button'
              : 'department_tree_expand_button'
            ) | transloco
          "
          [attr.data-testid]="'org-chart-toggle-' + node.department.id"
          (click)="toggleExpanded.emit(node.department.id)">
          <lib-chevron-down-icon
            [iconClass]="
              isExpanded
                ? 'size-4 text-neutral-500 rotate-180'
                : 'size-4 text-neutral-500'
            " />
        </button>
      }
    </div>

    @if (isExpanded && hasChildren) {
      <ul
        class="relative flex pt-xl before:absolute before:top-0 before:start-1/2 before:h-xl before:border-s before:border-neutral-300 before:content-['']">
        @for (child of node.children; track child.department.id) {
          <ng-container
            *ngTemplateOutlet="
              departmentBranch;
              context: { $implicit: child }
            " />
        }
        @for (positionNode of positions; track positionNode.position.id) {
          <ng-container
            *ngTemplateOutlet="
              positionBranch;
              context: { $implicit: positionNode }
            " />
        }
      </ul>
    }
  </li>
</ng-template>

<ng-template #positionBranch let-positionNode>
  <li
    class="relative flex flex-col items-center px-sm pt-xl before:absolute before:top-0 before:end-1/2 before:h-xl before:w-1/2 before:border-t before:border-neutral-300 before:content-[''] after:absolute after:top-0 after:start-1/2 after:h-xl after:w-1/2 after:border-s after:border-t after:border-neutral-300 after:content-[''] first:before:border-t-0 last:after:border-t-0 only:before:hidden">
    <div
      class="flex flex-col gap-sm min-w-44 max-w-56 p-md bg-white border border-neutral-200 rounded-lg"
      [attr.data-testid]="'org-chart-position-' + positionNode.position.id">
      <p
        class="text-sm font-semibold text-neutral-900 truncate"
        libTooltip
        [libTooltip]="positionNode.position.name"
        [hasCloseIcon]="false"
        placement="top"
        toolTipAction="mouseenter"
        [toolTipClass]="nameTooltipClass">
        {{ positionNode.position.name }}
        @if (positionNode.position.job_title?.radford_grade; as grade) {
          ({{ grade.label }})
        }
      </p>
      <div class="flex items-center gap-sm text-xs text-neutral-700">
        @if (positionNode.position.incumbent?.image) {
          <img
            [src]="positionNode.position.incumbent.image.url"
            [alt]="positionNode.position.incumbent.name"
            width="20"
            height="20"
            class="rounded-sm size-5" />
        } @else {
          <span
            class="rounded-sm bg-primary-50 text-primary-500 flex justify-center items-center size-5"
            libTooltip
            [libTooltip]="
              positionNode.position.incumbent?.name ??
              positionNode.position.name
            "
            [hasCloseIcon]="false"
            placement="top"
            toolTipAction="mouseenter"
            [toolTipClass]="nameTooltipClass">
            {{
              positionNode.position.incumbent?.name ??
                positionNode.position.name | avatar
            }}
          </span>
        }
        <span>
          {{
            positionNode.position.incumbent?.name ??
              ('department_org_chart_vacant_label' | transloco)
          }}
        </span>
      </div>
    </div>
    @if (positionNode.children.length) {
      <ul
        class="relative flex pt-xl before:absolute before:top-0 before:start-1/2 before:h-xl before:border-s before:border-neutral-300 before:content-['']">
        @for (child of positionNode.children; track child.position.id) {
          <ng-container
            *ngTemplateOutlet="positionBranch; context: { $implicit: child }" />
        }
      </ul>
    }
  </li>
</ng-template>
